import { useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function Icon({ name }: { name: string }) {
  const paths: Record<string, React.ReactNode> = {
    dashboard: (
      <path
        d="M3 3h7v7H3V3zm11 0h7v7h-7V3zm0 11h7v7h-7v-7zM3 14h7v7H3v-7z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    ),
    users: (
      <>
        <circle
          cx="9"
          cy="7"
          r="3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M3 18v-2a4 4 0 014-4h4a4 4 0 014 4v2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <circle
          cx="16"
          cy="7"
          r="2.5"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M19 13v1a3 3 0 01-3 3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </>
    ),
    plans: (
      <>
        <rect
          x="4"
          y="3"
          width="13"
          height="16"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <line
          x1="7"
          y1="8"
          x2="14"
          y2="8"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <line
          x1="7"
          y1="11"
          x2="14"
          y2="11"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <line
          x1="7"
          y1="14"
          x2="11"
          y2="14"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </>
    ),
    messages: (
      <>
        <path
          d="M4 4h13a2 2 0 012 2v7a2 2 0 01-2 2H8l-4 4V6a2 2 0 012-2z"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinejoin="round"
        />
        <line
          x1="7"
          y1="9"
          x2="14"
          y2="9"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <line
          x1="7"
          y1="12"
          x2="11"
          y2="12"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </>
    ),
    bookings: (
      <>
        <rect
          x="3"
          y="5"
          width="15"
          height="14"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <line
          x1="3"
          y1="9"
          x2="18"
          y2="9"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <line
          x1="7"
          y1="3"
          x2="7"
          y2="7"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <line
          x1="14"
          y1="3"
          x2="14"
          y2="7"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <circle cx="8" cy="13" r="1" fill="currentColor" />
        <circle cx="12" cy="13" r="1" fill="currentColor" />
        <circle cx="16" cy="13" r="1" fill="currentColor" />
      </>
    ),
    calendar: (
      <>
        <rect
          x="3"
          y="4"
          width="15"
          height="14"
          rx="2"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <line
          x1="3"
          y1="9"
          x2="18"
          y2="9"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <line
          x1="7"
          y1="2"
          x2="7"
          y2="5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <line
          x1="14"
          y1="2"
          x2="14"
          y2="5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
        <circle cx="7.5" cy="13" r="1.5" fill="currentColor" />
        <circle cx="13.5" cy="13" r="1.5" fill="currentColor" />
      </>
    ),
    settings: (
      <>
        <circle
          cx="10.5"
          cy="10.5"
          r="3"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.8"
        />
        <path
          d="M10.5 1.5v2m0 14v2M1.5 10.5h2m14 0h2M3.3 3.3l1.4 1.4m10.6 10.6l1.4 1.4M3.3 17.7l1.4-1.4m10.6-10.6l1.4-1.4"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </>
    ),
  };

  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 21 21"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0 }}
    >
      {paths[name]}
    </svg>
  );
}

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: "dashboard" },
  { to: "/calendar", label: "Calendar", icon: "calendar" },
  { to: "/users", label: "Users", icon: "users" },
  { to: "/plans", label: "Plans", icon: "plans" },
  { to: "/messages", label: "Messages", icon: "messages" },
  { to: "/bookings", label: "Bookings", icon: "bookings" },
  { to: "/settings", label: "Settings", icon: "settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [collapsed, setCollapsed] = useState(false);

  const sidebarWidth = collapsed ? 64 : 260;

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      {/* Sidebar */}
      <aside
        style={{
          width: sidebarWidth,
          minWidth: sidebarWidth,
          backgroundColor: "#0f0f23",
          color: "#fff",
          display: "flex",
          flexDirection: "column",
          transition: "width 0.25s ease, min-width 0.25s ease",
          overflow: "hidden",
          position: "relative",
        }}
      >
        {/* Logo / Brand */}
        <div
          style={{
            padding: collapsed ? "1.25rem 0" : "1.25rem 1.25rem",
            borderBottom: "1px solid rgba(255,255,255,0.08)",
            display: "flex",
            alignItems: "center",
            justifyContent: collapsed ? "center" : "space-between",
            gap: 12,
            minHeight: 64,
          }}
        >
          {!collapsed && (
            <h1
              style={{
                margin: 0,
                fontSize: "1.15rem",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                whiteSpace: "nowrap",
              }}
            >
              <span style={{ color: "#ff6b35" }}>Forge</span> Trainer
            </h1>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            style={{
              background: "rgba(255,255,255,0.06)",
              border: "none",
              color: "#a0a0c0",
              cursor: "pointer",
              borderRadius: 6,
              width: 32,
              height: 32,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 14,
              flexShrink: 0,
              transition: "background 0.15s",
            }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.12)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.background = "rgba(255,255,255,0.06)")
            }
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {collapsed ? "→" : "←"}
          </button>
        </div>

        {/* Nav Items */}
        <nav style={{ flex: 1, padding: "0.75rem 0.5rem" }}>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              style={({ isActive }) => ({
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: collapsed ? "0.7rem 0" : "0.7rem 1rem",
                justifyContent: collapsed ? "center" : "flex-start",
                color: isActive ? "#fff" : "#7a7a9e",
                backgroundColor: isActive
                  ? "rgba(255,107,53,0.15)"
                  : "transparent",
                borderRadius: 8,
                textDecoration: "none",
                fontWeight: isActive ? 600 : 400,
                fontSize: "0.9rem",
                marginBottom: 2,
                transition: "background 0.15s, color 0.15s",
                whiteSpace: "nowrap",
                position: "relative",
              })}
              onMouseEnter={(e) => {
                if (!e.currentTarget.style.backgroundColor.includes("0.15")) {
                  e.currentTarget.style.backgroundColor =
                    "rgba(255,255,255,0.06)";
                }
              }}
              onMouseLeave={(e) => {
                if (!e.currentTarget.style.backgroundColor.includes("0.15")) {
                  e.currentTarget.style.backgroundColor = "transparent";
                }
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 24,
                  flexShrink: 0,
                  color: "currentColor",
                }}
              >
                <Icon name={item.icon} />
              </span>
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        {/* Collapse hint at bottom */}
        {collapsed && (
          <div
            style={{
              padding: "1rem 0",
              textAlign: "center",
              fontSize: 10,
              color: "#4a4a6e",
            }}
          >
            ▲
          </div>
        )}
      </aside>

      {/* Main Content Area */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
        {/* Top Bar */}
        <header
          style={{
            height: "64px",
            backgroundColor: "#fff",
            borderBottom: "1px solid #e8e8ef",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "0 1.75rem",
            boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
          }}
        >
          <div style={{ fontSize: "0.95rem", color: "#555" }}>
            Welcome back,{" "}
            <span style={{ fontWeight: 600, color: "#1a1a2e" }}>
              {user?.name || "Trainer"}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            {/* Avatar */}
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #ff6b35, #ff8f65)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "0.85rem",
                fontWeight: 700,
                color: "#fff",
                boxShadow: "0 2px 6px rgba(255,107,53,0.3)",
              }}
            >
              {user?.name?.charAt(0).toUpperCase() || "T"}
            </div>
            {/* Logout */}
            <button
              onClick={logout}
              style={{
                padding: "0.45rem 1rem",
                backgroundColor: "transparent",
                border: "1px solid #e0e0e8",
                borderRadius: 6,
                cursor: "pointer",
                fontSize: "0.825rem",
                color: "#666",
                fontWeight: 500,
                transition: "all 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f5f5fa";
                e.currentTarget.style.borderColor = "#d0d0d8";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "transparent";
                e.currentTarget.style.borderColor = "#e0e0e8";
              }}
            >
              Logout
            </button>
          </div>
        </header>

        {/* Page Content */}
        <main
          style={{
            flex: 1,
            padding: "1.5rem",
            backgroundColor: "#f4f4f8",
            overflow: "auto",
          }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}
